"use client"

import { useState } from "react"
import { Loader2, Calendar, CheckCircle, XCircle } from "lucide-react"
import { RevealOnScroll } from "@/components/reveal-on-scroll"

export function OnlineAdmissionForm() {
    const [formData, setFormData] = useState({
        branchName: "",
        lastName: "",
        firstName: "",
        fatherName: "",
        motherName: "",
        phoneNo: "",
        whatsappNo: "",
        aadharNo: "",
        email: "",
        dob: "",
        gender: "",
        medium: "",
        standard: "",
        address: ""
    })

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitStatus, setSubmitStatus] = useState<{
        type: "success" | "error" | null
        message: string
    }>({ type: null, message: "" })

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)
        setSubmitStatus({ type: null, message: "" })

        try {
            // Using URLSearchParams for maximum compatibility with PHP $_POST
            const params = new URLSearchParams()

            // --- DATA PREPARATION ---
            // Reverting to YYYY-MM-DD (Native HTML format) which worked in previous successful submissions
            const dob = formData.dob
            const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD

            // Derive std_type based on standard
            let stdType = "Primary"
            const stdLower = formData.standard.toLowerCase()
            if (stdLower.includes("play") || stdLower.includes("nursery") || stdLower.includes("kg")) {
                stdType = "Pre-Primary"
            } else if (["5", "6", "7", "8", "9", "10"].includes(formData.standard)) {
                stdType = "Secondary"
            }

            // --- APPENDING FIELDS EXACTLY AS PER SQL COLUMNS ---
            params.append("branch", formData.branchName)
            params.append("last_name", formData.lastName)
            params.append("first_name", formData.firstName)
            params.append("father_name", formData.fatherName)
            params.append("aadhar_num", formData.aadharNo)
            params.append("contact_no", formData.phoneNo)
            params.append("gender", formData.gender)
            params.append("whatsapp_no", formData.whatsappNo)
            params.append("dob", dob)
            params.append("address", formData.address)
            params.append("email_id", formData.email)
            params.append("mother_guardians_name", formData.motherName)
            params.append("mdm", formData.medium)
            params.append("std", formData.standard)
            params.append("std_type", stdType)
            params.append("date", today)

            console.log("📤 Submitting form data (URLSearchParams)...", Object.fromEntries(params.entries()))

            const response = await fetch("https://dextro-online.com/lokbhartieducation.in/API/post_admission.php", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: params.toString(),
            })

            console.log("📥 Response status:", response.status)
            const responseText = await response.text()
            console.log("📥 Response text:", responseText)

            let result
            try {
                result = JSON.parse(responseText)
            } catch (e) {
                if (response.ok && (responseText.toLowerCase().includes("success") || responseText.toLowerCase().includes("submitted"))) {
                    result = { success: true, message: "Successfully Submitted" }
                } else {
                    throw new Error(responseText || "Server Error (500)")
                }
            }

            if ((result.status === 'success' || result.success === true || result.status === '1') && !result.message?.toLowerCase().includes("error")) {
                const enrollId = result.enroll_id || result.Enroll || result.id || ""

                setSubmitStatus({
                    type: "success",
                    message: `Application submitted successfully! Redirecting to your admission form (Enroll ID: ${enrollId})...`
                })

                // Redirect to the view page after a short delay
                setTimeout(() => {
                    window.location.href = `https://dextro-online.com/lokbhartieducation.in/view_admission.php?Enroll=${enrollId}`
                }, 1500)

                // Reset form
                setFormData({
                    branchName: "",
                    lastName: "",
                    firstName: "",
                    fatherName: "",
                    motherName: "",
                    phoneNo: "",
                    whatsappNo: "",
                    aadharNo: "",
                    email: "",
                    dob: "",
                    gender: "",
                    medium: "",
                    standard: "",
                    address: ""
                })
            } else {
                throw new Error(result.message || "Submission failed")
            }

        } catch (error: any) {
            console.error("❌ Submission error:", error)
            setSubmitStatus({
                type: "error",
                message: error.message || "Failed to submit application."
            })
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="bg-white">
            <RevealOnScroll animationClass="animate-fade-in">
                <h2 className="text-xl md:text-2xl font-serif text-center text-[#002147] mb-8">
                    School Admission Form session year 2026-2027
                </h2>
            </RevealOnScroll>

            <div className="flex flex-col gap-8">
                {/* Instructions Panel */}
                <RevealOnScroll animationClass="animate-slide-in-up">
                    <div className="w-full">
                        <div className="border border-blue-400 rounded overflow-hidden">
                            <div className="bg-[#4a90e2] text-white font-bold py-2 px-4">
                                INSTRUCTIONS
                            </div>
                            <div className="p-4 text-xs md:text-sm text-gray-700 leading-relaxed space-y-2">
                                <p className="font-semibold">Register With Lokmanya High School & Jr. College</p>
                                <ol className="list-decimal pl-4 space-y-2">
                                    <li>Fill The Form with Lokmanya High School & Jr. College you will receive Your Application No. on the registered Email ID and Mobile Number.</li>
                                    <li>Please fill mandatory details and make selection of Courses which you desire.</li>
                                    <li>Print the form for the Courses for which you have applied.</li>
                                    <li>After merit list submit the Form to the Lokmanya High School & Jr. College with required Documents and with processing fees RS.200.</li>
                                    <li>For Any further assistance Contact on: 8888573608 Contact Timing: 11am to 4pm</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </RevealOnScroll>

                {/* Status Message */}
                {submitStatus.type && (
                    <div className={`p-4 rounded-lg flex items-start gap-3 ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
                        }`}>
                        {submitStatus.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        ) : (
                            <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        )}
                        <p>{submitStatus.message}</p>
                    </div>
                )}

                {/* Form Fields */}
                <RevealOnScroll animationClass="animate-fade-in">
                    <div className="w-full">
                        <form onSubmit={handleSubmit} className="space-y-4">

                            {/* Branch Name */}
                            <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[120px] font-medium flex items-center">
                                    <span className="text-red-500 mr-1">*</span> Branch Name
                                </div>
                                <select
                                    name="branchName"
                                    value={formData.branchName}
                                    onChange={handleInputChange}
                                    className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm text-gray-600 w-full"
                                    required
                                >
                                    <option value="">Select One</option>
                                    <option value="Lokmanya High School & Jr. College Nallasopara">Lokmanya High School & Jr. College Nallasopara</option>
                                    <option value="Bharti Academy High School & Jr. College Umergoan">Bharti Academy High School & Jr. College Umergoan</option>
                                    <option value="Bharti Academy English High School & Jr. College Virar">Bharti Academy English High School & Jr. College Virar</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Last Name */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[100px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Last Name
                                    </div>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                        required
                                    />
                                </div>

                                {/* First Name */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[100px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> First Name
                                    </div>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Father's Name */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[120px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Father's Name
                                    </div>
                                    <input
                                        type="text"
                                        name="fatherName"
                                        value={formData.fatherName}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                        required
                                    />
                                </div>

                                {/* Mother's Name */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[120px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Mother's Name
                                    </div>
                                    <input
                                        type="text"
                                        name="motherName"
                                        value={formData.motherName}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Phone No */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[100px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Phone No.
                                    </div>
                                    <input
                                        type="tel"
                                        name="phoneNo"
                                        value={formData.phoneNo}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                        required
                                    />
                                </div>

                                {/* Whatsapp No */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[120px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Whatsapp No.
                                    </div>
                                    <input
                                        type="tel"
                                        name="whatsappNo"
                                        value={formData.whatsappNo}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Gender and Aadhar */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Gender - ADDED FIELD */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[100px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Gender
                                    </div>
                                    <select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm text-gray-600 w-full"
                                        required
                                    >
                                        <option value="">Select One</option>
                                        <option value="MALE">Male</option>
                                        <option value="FEMALE">Female</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>

                                {/* Aadhar Number */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[120px] font-medium flex items-center">
                                        Aadhar Number
                                    </div>
                                    <input
                                        type="text"
                                        name="aadharNo"
                                        value={formData.aadharNo}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Email Id */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[80px] font-medium flex items-center">
                                        Email Id
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm w-full"
                                    />
                                </div>

                                {/* DOB */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[60px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> DOB
                                    </div>
                                    <div className="flex-1 relative w-full">
                                        <input
                                            type="date"
                                            name="dob"
                                            value={formData.dob}
                                            onChange={handleInputChange}
                                            placeholder="mm/dd/yyyy"
                                            className="w-full px-4 py-2 bg-white focus:outline-none text-sm text-gray-600 appearance-none"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Medium */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[80px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Medium
                                    </div>
                                    <select
                                        name="medium"
                                        value={formData.medium}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm text-gray-600 w-full"
                                        required
                                    >
                                        <option value="">Select One</option>
                                        <option value="English">English</option>
                                        <option value="Marathi">Marathi</option>
                                        <option value="Semi-English">Semi-English</option>
                                    </select>
                                </div>

                                {/* Standard */}
                                <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                    <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[100px] font-medium flex items-center">
                                        <span className="text-red-500 mr-1">*</span> Standard
                                    </div>
                                    <select
                                        name="standard"
                                        value={formData.standard}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm text-gray-600 w-full"
                                        required
                                    >
                                        <option value="">Select One</option>
                                        <option value="Play group">Play group</option>
                                        <option value="Nursery">Nursery</option>
                                        <option value="Jr. KG">Jr. KG</option>
                                        <option value="Sr. KG">Sr. KG</option>
                                        <option value="1">1st</option>
                                        <option value="2">2nd</option>
                                        <option value="5">5th</option>
                                        <option value="8">8th</option>
                                        <option value="9">9th</option>
                                        <option value="10">10th</option>
                                    </select>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="flex flex-col sm:flex-row border border-gray-300 rounded overflow-hidden">
                                <div className="bg-[#e9e9e9] px-3 py-2 border-b sm:border-b-0 sm:border-r border-gray-300 text-sm text-gray-700 min-w-[90px] font-medium flex items-center sm:items-start sm:pt-3">
                                    <span className="text-red-500 mr-1">*</span> Address
                                </div>
                                <textarea
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    className="flex-1 px-4 py-2 bg-white focus:outline-none text-sm resize-none w-full"
                                    rows={2}
                                    required
                                />
                            </div>


                            {/* Submit Button */}
                            <div className="flex justify-center pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-[#428bca] hover:bg-[#3071a9] text-white px-6 py-2 rounded shadow-sm text-sm font-medium transition-colors"
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Submitting...
                                        </span>
                                    ) : (
                                        "Submit"
                                    )}
                                </button>
                            </div>

                        </form>
                    </div>
                </RevealOnScroll>
            </div>
        </div>
    )
}
